import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/sharp-light-svg-icons';
import { faChevronRight, faChevronLeft } from '@fortawesome/sharp-solid-svg-icons';

import './styles/Results.css';

import { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { haNavigation, pages } from '../assets/constants';
import { useFetchChallengeQuery } from '../challenges/challengesApiSlice';
import { emptyChallenge, setChallenge } from '../challenges/challengesSlice';
import { useFetchTakeActionQuery } from '../forYou/mediaSlidersApiSlice';
import { setTakeAction } from '../forYou/mediaSlidersSlice';
import { Slider } from '../shared/components/slider/Slider';
import LoadingSpinner from '../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../shared/context/auth-context';
import { HeaderNavigation } from '../shared/headerNavigation/HeaderNavigation';
import { useHttpClient } from '../shared/hooks/http-hook';
import WhitePopUpNoTitle from '../shared/modal/whitePopUp/WhitePopUpNoTitle';
import { getChallengeType } from '../shared/util/challenges';
import {
  formatAssessmentDate,
  formatDate,
  formatDateRange,
  getPeriodInDates,
} from '../shared/util/helperfunctions';
import Trackers from '../trackers/Trackers';
import EnHeroImg from './images/Energy.jpg';
import FiHeroImg from './images/Financial_Wellbeing.jpg';
import HyHeroImg from './images/Hydration.jpg';
import MeHeroImg from './images/Mental_Wellbeing.jpg';
import MoHeroImg from './images/Movement.jpg';
import NuHeroImg from './images/Nutrition.jpg';
import PWHeroImg from './images/Physical_Wellbeing.jpg';
import PrHeroImg from './images/Productivity.jpg';
import SlHeroImg from './images/Sleep.jpg';
import EnergyResults from './resultsByHealthArea/EnergyResults';
import FinancialWellbeingResults from './resultsByHealthArea/FinancialWellbeingResults';
import HydrationResults from './resultsByHealthArea/HydrationResults';
import MentalWellbeingResults from './resultsByHealthArea/MentalWellbeingResults';
import MovementResults from './resultsByHealthArea/MovementResults';
import NutritionResults from './resultsByHealthArea/NutritionResults';
import PhysicalWellbeingResults from './resultsByHealthArea/PhysicalWellbeingResults';
import ProductivityResults from './resultsByHealthArea/ProductivityResults';
import SleepResults from './resultsByHealthArea/SleepResults';
import { applyAssessmentQaMock } from './utils/qaAssessmentMocks';

const trackerPeriodArray = ['week', 'month', 'year'];

function HealthArea() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageid = window.location.pathname;
  const healthArea = pageid.split('/')[1];
  const pageName = pageid.split('/')[1].replaceAll('-', ' ');
  const activePage = pageid.split('/')[2];
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const location = useLocation();
  const { state, search } = location;
  const takeAction = useSelector((reduxState) => reduxState.mediaSliders.takeAction);
  const challenge = useSelector((reduxState) => reduxState?.challenges?.challenge);

  const [isLoading, setIsLoading] = useState(false);
  const [showTrackers, setShowTrackers] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [keepTrackerOpen, setKeepTrackerOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTextContent, setModalTextContent] = useState('');
  const [updateSmoking, setUpdateSmoking] = useState(false);
  const [assessmentNumber, setAssessmentNumber] = useState(null);
  const [assessmentNumberMax, setAssessmentNumberMax] = useState(null);

  const [defaultDate, setDefaultDate] = useState(null);
  const [activePeriod, setActivePeriod] = useState(null);
  const [datesString, setDatesString] = useState(null);

  const pageObjRef = useRef({
    heroImage: '',
    heroTitle: '',
    heroText: '',
    assessDates: [],
    assessmentNumber: '',
    trackerNames: [],
  });

  const [dataObj, setDataObj] = useState({
    isLoading: false,
    assessScores: '',
    trackerScores: [],
    trackerJournals: [],
    slidersList: [],
  });

  useEffect(() => {
    if (!healthArea) return;
    const pageObj = pageObjRef.current;
    switch (healthArea) {
      case 'physical-wellbeing':
        pageObj.heroImage = PWHeroImg;
        pageObj.heroTitle = t('DW001');
        pageObj.heroText = t('DW002');
        pageObj.trackerNames = ['smoking'];
        break;
      case 'energy':
        pageObj.heroImage = EnHeroImg;
        pageObj.heroTitle = t('DW021');
        pageObj.heroText = t('DW022');
        pageObj.trackerNames = ['energy'];
        break;
      case 'financial-wellbeing':
        pageObj.heroImage = FiHeroImg;
        pageObj.heroTitle = t('DW012');
        pageObj.heroText = t('DW013');
        break;
      case 'hydration-&-alcohol':
        pageObj.heroImage = HyHeroImg;
        pageObj.heroTitle = t('DW016');
        pageObj.heroText = t('DW017');
        pageObj.trackerNames = ['hydration', 'alcohol'];
        break;
      case 'mental-wellbeing':
        pageObj.heroImage = MeHeroImg;
        pageObj.heroTitle = t('DW010');
        pageObj.heroText = t('DW011');
        pageObj.trackerNames = ['mood', 'stress'];
        break;
      case 'movement':
        pageObj.heroImage = MoHeroImg;
        pageObj.heroTitle = t('DW006');
        pageObj.heroText = t('DW007');
        pageObj.trackerNames = ['activity'];
        break;
      case 'nutrition':
        pageObj.heroImage = NuHeroImg;
        pageObj.heroTitle = t('DW014');
        pageObj.heroText = t('DW015');
        pageObj.trackerNames = ['nutrition'];
        break;
      case 'work-&-productivity':
        pageObj.heroImage = PrHeroImg;
        pageObj.heroTitle = t('DW019');
        pageObj.heroText = t('DW020');
        pageObj.trackerNames = ['productivity'];
        break;
      case 'sleep-&-rest':
        pageObj.heroImage = SlHeroImg;
        pageObj.heroTitle = t('DW008');
        pageObj.heroText = t('DW009');
        pageObj.trackerNames = ['sleep'];
        break;
      default:
        break;
    }
    const fetchData = async () => {
      setIsLoading(true);
      const today = new Date().toJSON().slice(0, 10);
      setDefaultDate(today);
      setActivePeriod(t('DW003'));
      const currentPeriod = getPeriodInDates(today, 'week');
      const string = formatDateRange(currentPeriod, 'week');
      setDatesString(string);
      await getAssessmentScores();
      if (pageObjRef.current.heroTitle !== t('DW012')) {
        await getTrackerData(currentPeriod);
      } else {
        setIsLoading(false);
        setShowAssessment(true);
      }
    };

    fetchData();
  }, [healthArea]);

  useEffect(() => {
    if (dataObj.trackerScores[pageObjRef.current.trackerNames[0]]) {
      let hasAverage = Object.entries(
        dataObj.trackerScores[pageObjRef.current.trackerNames[0]]
      ).some(([key, value]) => key.includes('_average') && value !== null);
      if (pageObjRef.current.trackerNames.length > 1 && !hasAverage) {
        hasAverage = Object.entries(dataObj.trackerScores[pageObjRef.current.trackerNames[1]]).some(
          ([key, value]) => key.includes('_average') && value !== null
        );
      }
      if (activePage === 'track') {
        setShowTrackers(true);
        setShowAssessment(false);
      } else {
        setShowTrackers(false);
        if (state?.from === 'tracker') {
          setShowAssessment(false);
        } else if (hasAverage) {
          setShowAssessment(false);
        } else if (keepTrackerOpen) {
          setShowAssessment(false);
        } else {
          setShowAssessment(true);
        }
      }
      setIsLoading(false);
    }
  }, [
    healthArea,
    activePage,
    state?.from,
    dataObj.trackerScores[pageObjRef.current.trackerNames[0]],
  ]);

  const section = pages.find((page) => page.url === healthArea);
  const sectionid = section.id;

  const { data: takeActionData, isLoading: isFetchingTakeAction } = useFetchTakeActionQuery(
    {
      token: auth.token,
      pageid: sectionid,
    },
    { skip: !sectionid }
  );

  const {
    data: challengeData,
    isLoading: isChallengeDataLoading,
    error: challengeDataError,
  } = useFetchChallengeQuery(
    {
      token: auth.token,
      challengeType: getChallengeType(sectionid),
    },
    { skip: !getChallengeType(sectionid), refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (
      !isChallengeDataLoading &&
      !challengeDataError &&
      challengeData &&
      !challengeData?.message &&
      challengeData?.start_date &&
      new Date().getTime() >= new Date(challengeData.start_date).getTime()
    ) {
      dispatch(
        setChallenge({
          challenge: challengeData,
        })
      );
    } else {
      dispatch(
        setChallenge({
          challenge: emptyChallenge,
        })
      );
    }
  }, [challengeData, isChallengeDataLoading, challengeDataError]);

  useEffect(() => {
    if (!isFetchingTakeAction && takeActionData) {
      dispatch(setTakeAction(takeActionData));
    }
  }, [dispatch, isFetchingTakeAction]);

  useEffect(() => {
    const pageCont = document.getElementsByClassName('page-cont');
    if (pageCont.length > 0 && healthArea && activePage && !isFetchingTakeAction) {
      pageCont[0].classList.add('visible');
    }
  }, [healthArea, activePage, isFetchingTakeAction]);

  const getPreviousAssessment = () => {
    const newAssessment = pageObjRef.current.assessmentNumber - 1;
    if (newAssessment >= 0) {
      getAssessmentScores(newAssessment);
    }
  };

  const getLaterAssessment = () => {
    const newAssessment = pageObjRef.current.assessmentNumber + 1;
    if (newAssessment < pageObjRef.current.assessDates.length) {
      getAssessmentScores(newAssessment);
    }
  };

  const openModal = useCallback(
    (title, text) => {
      setModalIsOpen(true);
      setModalTextContent({ title, text });
    },
    [modalIsOpen, modalTextContent]
  );

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, [modalIsOpen]);

  const getAssessmentScores = async (index) => {
    let url = `${process.env.REACT_APP_BACKEND_URL}/assessments/individualReportContent/${auth.userId}`;
    if (index !== undefined) {
      url = `${process.env.REACT_APP_BACKEND_URL}/assessments/individualReportContent/${auth.userId}/${pageObjRef.current.assessDates[index]}`;
    }

    try {
      const response = await sendRequest(url);
      if (response.scores) {
        // Local QA mocking is opt-in via URL params and disabled in production.
        const mockedScores = applyAssessmentQaMock(response.scores, search, {
          isEnabled: process.env.NODE_ENV !== 'production',
        });
        setDataObj((prev) => ({ ...prev, assessScores: mockedScores }));

        if (pageObjRef.current.assessDates.length < 1) {
          pageObjRef.current.assessDates = response.dates;
        }

        if (index === undefined && !pageObjRef.current.assessmentNumber) {
          const lastAssessment = response.dates.length - 1;
          pageObjRef.current.assessmentNumber = lastAssessment;
          setAssessmentNumber(lastAssessment);
          setAssessmentNumberMax(lastAssessment);
        } else {
          pageObjRef.current.assessmentNumber = index;
          setAssessmentNumber(index);
        }
      }
    } catch (err) {}
  };

  const getTrackerDataByName = async (name, period, trackerDates) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${name}/${trackerDates[0]}/${trackerDates[1]}${
        period ? `/${period}` : ``
      }`;

      const response = await sendRequest(url);

      if (response) {
        return {
          score: { [name]: response },
          journals: response.journals,
        };
      }
      return undefined;
    } catch (err) {}
    return undefined;
  };

  const getTrackerData = async (trackerDates, period) => {
    const pageObj = pageObjRef.current;
    let scores = {};
    let journals = [];
    const promises = [];

    pageObj.trackerNames.forEach((name) => {
      promises.push(getTrackerDataByName(name, period, trackerDates));
    });

    const response = await Promise.all(promises);

    response.forEach((data) => {
      scores = { ...scores, ...data.score };
      journals = journals.concat(data.journals);
    });

    setDataObj((prev) => ({
      ...prev,
      trackerScores: scores,
      trackerJournals: journals,
    }));
  };

  // scroll top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activePage]);

  const handleCloseTrackers = () => {
    if (state?.from === 'challenge' && challenge?.isActive) {
      navigate(`/${healthArea}/${haNavigation[3].title}`);
      return;
    }
    if (dataObj?.trackerScores?.smoking?.smoke_per_day?.length > 0) {
      setUpdateSmoking(true);
    }

    const today = new Date().toJSON().slice(0, 10);
    setDefaultDate(today);
    setActivePeriod(t('DW003'));
    const currentPeriod = getPeriodInDates(today, 'week');
    const string = formatDateRange(currentPeriod, 'week');
    setDatesString(string);
    getTrackerData(currentPeriod);
    setShowAssessment(false);
    navigate(`/${healthArea}/${haNavigation[1].title}`, {
      state: { from: 'tracker' },
    });
  };

  const toggeleShowAssessment = (bool) => {
    setShowAssessment(bool);
  };

  const getOptionName = (period) => {
    if (period === 'week') return t('DW003');
    if (period === 'month') return t('DW004');
    return t('DW005');
  };

  const handleChangePeriod = (e) => {
    const period = e.target.value;
    let newPeriod;
    let string;
    if (period === 'week') {
      setActivePeriod(t('DW003'));
      newPeriod = getPeriodInDates(defaultDate, 'week');
      string = formatDateRange(newPeriod, 'week');
      getTrackerData(newPeriod);
    } else if (period === 'month') {
      setActivePeriod(t('DW004'));
      newPeriod = getPeriodInDates(defaultDate, 'month');
      string = formatDateRange(newPeriod, 'month');
      getTrackerData(newPeriod, 'weekly');
    } else if (period === 'year') {
      setActivePeriod(t('DW005'));
      newPeriod = getPeriodInDates(defaultDate, 'year');
      string = formatDateRange(newPeriod, 'year');
      getTrackerData(newPeriod, 'monthly');
    }
    setKeepTrackerOpen(true);
    setDatesString(string);
  };

  const getPreviousPeriod = () => {
    // Initialize new default and tracker dates
    let newDefaultDate;
    let newTrackerDates;
    let newDatesString;

    const inputDate = new Date(defaultDate);
    switch (activePeriod) {
      case t('DW003'): // week
        inputDate.setDate(inputDate.getDate() - 7);
        newDefaultDate = formatDate(inputDate);
        newTrackerDates = getPeriodInDates(newDefaultDate, 'week');
        newDatesString = formatDateRange(newTrackerDates, 'week');
        getTrackerData(newTrackerDates);
        break;

      case t('DW004'): // month
        inputDate.setMonth(inputDate.getMonth() - 1);
        newDefaultDate = formatDate(inputDate);
        newTrackerDates = getPeriodInDates(newDefaultDate, 'month');
        newDatesString = formatDateRange(newTrackerDates, 'month');
        getTrackerData(newTrackerDates, 'weekly');
        break;

      case t('DW005'): // year
        inputDate.setFullYear(inputDate.getFullYear() - 1);
        newDefaultDate = formatDate(inputDate);
        newTrackerDates = getPeriodInDates(newDefaultDate, 'year');
        newDatesString = formatDateRange(newTrackerDates, 'year');
        getTrackerData(newTrackerDates, 'monthly');
        break;

      default:
        throw new Error('Invalid tracker period specified.');
    }

    // Update the state values
    setKeepTrackerOpen(true);
    setDefaultDate(newDefaultDate);
    setDatesString(newDatesString);
  };

  const getLaterPeriod = () => {
    // Initialize new default and tracker dates
    let newDefaultDate;
    let newTrackerDates;
    let newDatesString;

    const inputDate = new Date(defaultDate);
    switch (activePeriod) {
      case t('DW003'):
        inputDate.setDate(inputDate.getDate() + 7);
        newDefaultDate = formatDate(inputDate);
        newTrackerDates = getPeriodInDates(newDefaultDate, 'week');
        newDatesString = formatDateRange(newTrackerDates, 'week');
        getTrackerData(newTrackerDates);
        break;

      case t('DW004'):
        inputDate.setMonth(inputDate.getMonth() + 1);
        newDefaultDate = formatDate(inputDate);
        newTrackerDates = getPeriodInDates(newDefaultDate, 'month');
        newDatesString = formatDateRange(newTrackerDates, 'month');
        getTrackerData(newTrackerDates, 'weekly');
        break;

      case t('DW005'):
        inputDate.setFullYear(inputDate.getFullYear() + 1);
        newDefaultDate = formatDate(inputDate);
        newTrackerDates = getPeriodInDates(newDefaultDate, 'year');
        newDatesString = formatDateRange(newTrackerDates, 'year');
        getTrackerData(newTrackerDates, 'monthly');
        break;

      default:
        throw new Error('Invalid tracker period specified.');
    }

    // Update the state values
    setKeepTrackerOpen(true);
    setDefaultDate(newDefaultDate);
    setDatesString(newDatesString);
  };

  return (
    <div id="main_container" className="foryou glowrm platform results">
      {isLoading && <LoadingSpinner asOverlay />}
      <HeaderNavigation
        bottomBarActionButton={
          healthArea !== 'financial-wellbeing' && {
            children: t('nav.button.track'),
            variant: 'secondary',
            onClick: () => {
              setShowTrackers(true);
            },
          }
        }
      />
      {showTrackers && <Trackers pageName={pageName} closeTrackers={handleCloseTrackers} />}
      {modalIsOpen && (
        <WhitePopUpNoTitle
          returnFocus
          popupContents={modalTextContent}
          ctaBtn={t('T015')}
          handleSubmit={modalClickHandler}
          dialogTitle={t('modal.results.explanation', { title: modalTextContent.title })}
        />
      )}
      <div className="page-cont">
        <div
          className="results-img"
          style={{
            background: `url(${pageObjRef.current.heroImage}) center/cover no-repeat`,
          }}
        >
          <div className="gradient-overlay">
            <div className="hero-contents">
              <h1 className="hero-title">{pageObjRef.current.heroTitle}</h1>
              <h2>{pageObjRef.current.heroText}</h2>
            </div>
            <div className="results-nav">
              <div className="results-menu" role="group" aria-label={t('results.menu.title')}>
                {pageObjRef?.current?.trackerNames[0] && (
                  <div className="results-toggle">
                    <button
                      type="button"
                      className={showAssessment ? 'assessment active' : 'assessment'}
                      onClick={() => {
                        toggeleShowAssessment(true);
                        setActivePeriod(t('DW003'));
                      }}
                    >
                      {t('D001')}
                    </button>
                    <button
                      type="button"
                      className={!showAssessment ? 'tracker active' : 'tracker'}
                      onClick={() => toggeleShowAssessment(false)}
                    >
                      {t('D002')}
                    </button>
                  </div>
                )}
                {!showAssessment && (
                  <button
                    type="button"
                    className="open-tracker"
                    onClick={() => {
                      setShowTrackers(true);
                      setActivePeriod(t('DW003'));
                    }}
                  >
                    <span className="visuallyHidden">{t('T577')}</span>
                    <FontAwesomeIcon icon={faPlus} className="add-tracker" />
                  </button>
                )}
              </div>
              {showAssessment && pageObjRef.current.assessDates.length > 0 && (
                <div
                  className="dates-toggle fs-exclude"
                  role="group"
                  aria-label={t('results.dates.toggle')}
                >
                  <button
                    type="button"
                    onClick={getPreviousAssessment}
                    className="transparent-button dates-toggle-button"
                    disabled={!assessmentNumber}
                    aria-label={`${pageObjRef.current.heroTitle} ${t('results.previous.assessment')}`}
                  >
                    <span className="visuallyHidden">{`${pageObjRef.current.heroTitle} ${t('results.previous.assessment')}`}</span>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <div role="alert" aria-live="polite" aria-atomic="true">
                    {formatAssessmentDate(
                      pageObjRef.current.assessDates[pageObjRef.current.assessmentNumber]
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={getLaterAssessment}
                    className="transparent-button dates-toggle-button"
                    disabled={assessmentNumber === null || assessmentNumber === assessmentNumberMax}
                    aria-label={`${pageObjRef.current.heroTitle} ${t('results.next.assessment')}`}
                  >
                    <span className="visuallyHidden">{`${pageObjRef.current.heroTitle} ${t('results.next.assessment')}`}</span>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}

              {!showAssessment && (
                <div className="results-menu tracker-data">
                  <div className="dates-toggle dates-toggle-select-container  with-dd fs-exclude">
                    <label className="dates-toggle-select-label" htmlFor="period-select">
                      {t('results.select.period')}
                    </label>
                    <select
                      className="data-toggle-select fs-exclude"
                      onChange={handleChangePeriod}
                      id="period-select"
                    >
                      {trackerPeriodArray.map((period) => (
                        <option className="period-option" key={period} value={period}>
                          {getOptionName(period)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    className="dates-toggle fs-exclude"
                    role="group"
                    aria-label={t('results.dates.toggle')}
                  >
                    <button
                      type="button"
                      onClick={getPreviousPeriod}
                      className="transparent-button"
                      aria-labelledby="previous-period-label"
                    >
                      <span
                        id="previous-period-label"
                        className="visuallyHidden"
                      >{`${pageObjRef.current.heroTitle} ${t('results.previous.period')} ${activePeriod}`}</span>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    {datesString && (
                      <div role="alert" aria-live="polite" aria-atomic="true">
                        {datesString}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={getLaterPeriod}
                      className="transparent-button"
                      aria-labelledby="next-period-label"
                    >
                      <span
                        id="next-period-label"
                        className="visuallyHidden"
                      >{`${pageObjRef.current.heroTitle} ${t('results.next.period')} ${activePeriod}`}</span>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {healthArea === 'sleep-&-rest' && (
          <SleepResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
          />
        )}
        {healthArea === 'movement' && (
          <MovementResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
          />
        )}
        {healthArea === 'mental-wellbeing' && (
          <MentalWellbeingResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
            title={t('DW010')}
          />
        )}
        {healthArea === 'financial-wellbeing' && (
          <FinancialWellbeingResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
          />
        )}
        {healthArea === 'nutrition' && (
          <NutritionResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
            title={t('DW014')}
          />
        )}
        {healthArea === 'hydration-&-alcohol' && (
          <HydrationResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
            title={t('DW016')}
          />
        )}
        {healthArea === 'work-&-productivity' && (
          <ProductivityResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
          />
        )}
        {healthArea === 'energy' && (
          <EnergyResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
          />
        )}
        {healthArea === 'physical-wellbeing' && (
          <PhysicalWellbeingResults
            pageObjRef={pageObjRef}
            dataObj={dataObj}
            showAssessment={showAssessment}
            openModal={openModal}
            updateSmoking={updateSmoking}
            closeUpdateSmoking={() => setUpdateSmoking(false)}
          />
        )}
        <div className="take-action-cont">
          {takeAction?.length > 0 && (
            <Slider title={t('FY005')} id="grekljhot" media={takeAction} target={false} />
          )}
        </div>
      </div>
    </div>
  );
}

export default HealthArea;
